import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  TextField,
  FormControlLabel,
  Switch,
  Grid,
  IconButton,
  Stack,
  Divider,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Avatar,
  Chip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import PersonIcon from '@mui/icons-material/Person';
import { authService } from '../services/authService';
import { filesService } from '../services/filesService';
import { useAuthenticatedFilePreview } from '../hooks/useAuthenticatedFilePreview';
import { useNotification } from '../hooks/NotificationContext';
import type {
  Mitarbeiter,
  MitarbeiterProfilDaten,
  ProfilAbschlussEintrag,
  ProfilAbschlussArt,
  ProfilFaehigkeitEintrag,
  ProfilFaehigkeitNiveau,
} from '../types';

function emptyProfil(): MitarbeiterProfilDaten {
  return {
    handynummer: null,
    adresse_zeile1: null,
    plz: null,
    ort: null,
    land: null,
    geburtsdatum: null,
    profil_beschreibung: null,
    avatar_file_id: null,
    festnetz: null,
    kontakt_zeitfenster: null,
    zeitzone: null,
    sprachen: [],
    beruflicher_titel: null,
    link_linkedin: null,
    link_website: null,
    profil_abschluesse: [],
    profil_faehigkeiten: [],
    profil_oeffentlich_kontakt: false,
    profil_oeffentlich_adresse: false,
    profil_oeffentlich_geburtsdatum: false,
    profil_oeffentlich_avatar: false,
    profil_oeffentlich_beschreibung: false,
    profil_oeffentlich_erreichbarkeit: false,
    profil_oeffentlich_beruflich: false,
    profil_oeffentlich_kompetenzen: false,
  };
}

function normalizeProfil(p?: MitarbeiterProfilDaten | null): MitarbeiterProfilDaten {
  const e = emptyProfil();
  if (!p) return e;
  return {
    ...e,
    ...p,
    sprachen: [...(p.sprachen ?? [])],
    profil_abschluesse: [...(p.profil_abschluesse ?? [])],
    profil_faehigkeiten: [...(p.profil_faehigkeiten ?? [])],
  };
}

const ABS_ARTEN: { value: ProfilAbschlussArt; label: string }[] = [
  { value: 'studium', label: 'Studium' },
  { value: 'ausbildung', label: 'Ausbildung' },
  { value: 'zertifikat', label: 'Zertifikat' },
  { value: 'sonstiges', label: 'Sonstiges' },
];

const NIVEAUS: { value: ProfilFaehigkeitNiveau; label: string }[] = [
  { value: 'grundkenntnisse', label: 'Grundkenntnisse' },
  { value: 'fortgeschritten', label: 'Fortgeschritten' },
  { value: 'expertise', label: 'Expertise' },
];

export interface ProfilNutzerprofilSectionProps {
  user: Mitarbeiter;
  onUpdated: () => Promise<void>;
  accent: string;
}

const ProfilNutzerprofilSection: React.FC<ProfilNutzerprofilSectionProps> = ({ user, onUpdated, accent }) => {
  const { showSuccess } = useNotification();
  const [form, setForm] = useState<MitarbeiterProfilDaten>(() => normalizeProfil(user.profil));
  const [spracheInput, setSpracheInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setForm(normalizeProfil(user.profil));
  }, [user.id, user.profil]);

  const avatarUrl = useAuthenticatedFilePreview(form.avatar_file_id);

  const updateForm = <K extends keyof MitarbeiterProfilDaten>(key: K, value: MitarbeiterProfilDaten[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const abs = form.profil_abschluesse
        .map((a) => ({
          ...a,
          bezeichnung: a.bezeichnung.trim(),
        }))
        .filter((a) => a.bezeichnung.length > 0);
      const faeh = form.profil_faehigkeiten
        .map((f) => ({
          ...f,
          name: f.name.trim(),
        }))
        .filter((f) => f.name.length > 0);

      const payload: Partial<MitarbeiterProfilDaten> = {
        handynummer: form.handynummer,
        adresse_zeile1: form.adresse_zeile1,
        plz: form.plz,
        ort: form.ort,
        land: form.land,
        geburtsdatum: form.geburtsdatum,
        profil_beschreibung: form.profil_beschreibung,
        festnetz: form.festnetz,
        kontakt_zeitfenster: form.kontakt_zeitfenster,
        zeitzone: form.zeitzone,
        sprachen: [...form.sprachen],
        beruflicher_titel: form.beruflicher_titel,
        link_linkedin: form.link_linkedin,
        link_website: form.link_website,
        profil_abschluesse: abs,
        profil_faehigkeiten: faeh,
        profil_oeffentlich_kontakt: form.profil_oeffentlich_kontakt,
        profil_oeffentlich_adresse: form.profil_oeffentlich_adresse,
        profil_oeffentlich_geburtsdatum: form.profil_oeffentlich_geburtsdatum,
        profil_oeffentlich_avatar: form.profil_oeffentlich_avatar,
        profil_oeffentlich_beschreibung: form.profil_oeffentlich_beschreibung,
        profil_oeffentlich_erreichbarkeit: form.profil_oeffentlich_erreichbarkeit,
        profil_oeffentlich_beruflich: form.profil_oeffentlich_beruflich,
        profil_oeffentlich_kompetenzen: form.profil_oeffentlich_kompetenzen,
      };

      await authService.patchProfil(payload);
      await onUpdated();
      showSuccess('Profil gespeichert');
    } finally {
      setSaving(false);
    }
  };

  const handleAvatarPick = () => fileInputRef.current?.click();

  const handleAvatarFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    setAvatarBusy(true);
    try {
      await authService.uploadAvatar(f);
      await onUpdated();
      showSuccess('Profilbild aktualisiert');
    } finally {
      setAvatarBusy(false);
    }
  };

  const handleAvatarDelete = async () => {
    setAvatarBusy(true);
    try {
      await authService.deleteAvatar();
      await onUpdated();
      showSuccess('Profilbild entfernt');
    } finally {
      setAvatarBusy(false);
    }
  };

  const addAbschluss = () => {
    const n: ProfilAbschlussEintrag = { bezeichnung: '', institution: null, jahr: null, art: null };
    updateForm('profil_abschluesse', [...form.profil_abschluesse, n]);
  };

  const updateAbschluss = (i: number, patch: Partial<ProfilAbschlussEintrag>) => {
    const next = [...form.profil_abschluesse];
    next[i] = { ...next[i], ...patch };
    updateForm('profil_abschluesse', next);
  };

  const removeAbschluss = (i: number) => {
    updateForm(
      'profil_abschluesse',
      form.profil_abschluesse.filter((_, j) => j !== i),
    );
  };

  const addFaehigkeit = () => {
    const n: ProfilFaehigkeitEintrag = { name: '', niveau: null };
    updateForm('profil_faehigkeiten', [...form.profil_faehigkeiten, n]);
  };

  const updateFaehigkeit = (i: number, patch: Partial<ProfilFaehigkeitEintrag>) => {
    const next = [...form.profil_faehigkeiten];
    next[i] = { ...next[i], ...patch };
    updateForm('profil_faehigkeiten', next);
  };

  const removeFaehigkeit = (i: number) => {
    updateForm(
      'profil_faehigkeiten',
      form.profil_faehigkeiten.filter((_, j) => j !== i),
    );
  };

  const addSprache = () => {
    const t = spracheInput.trim();
    if (!t || form.sprachen.includes(t)) return;
    if (form.sprachen.length >= 20) return;
    updateForm('sprachen', [...form.sprachen, t]);
    setSpracheInput('');
  };

  const SwitchRow: React.FC<{ label: string; checked: boolean; onChange: (v: boolean) => void }> = ({
    label,
    checked,
    onChange,
  }) => (
    <FormControlLabel
      control={<Switch checked={checked} onChange={(_, v) => onChange(v)} size="small" />}
      label={label}
    />
  );

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <PersonIcon sx={{ color: accent }} />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Profil und Sichtbarkeit
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Diese Angaben sind optional. Schalter steuern, ob Kolleginnen und Kollegen die jeweiligen Blöcke im
          Kurzprofil sehen können. Standard ist privat.
        </Typography>

        <Stack spacing={2} sx={{ mb: 3 }} alignItems="flex-start">
          <Typography variant="subtitle2" fontWeight={600}>
            Profilbild
          </Typography>
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar src={avatarUrl} sx={{ width: 72, height: 72 }}>
              {!avatarUrl && form.avatar_file_id ? <PersonIcon /> : null}
            </Avatar>
            <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" hidden onChange={handleAvatarFile} />
            <Button
              variant="outlined"
              size="small"
              startIcon={<PhotoCameraIcon />}
              onClick={handleAvatarPick}
              disabled={avatarBusy}
            >
              Bild wählen
            </Button>
            {form.avatar_file_id && (
              <Button variant="text" color="error" size="small" onClick={handleAvatarDelete} disabled={avatarBusy}>
                Entfernen
              </Button>
            )}
          </Stack>
          <SwitchRow
            label="Profilbild für Kolleginnen und Kollegen sichtbar"
            checked={form.profil_oeffentlich_avatar}
            onChange={(v) => updateForm('profil_oeffentlich_avatar', v)}
          />
        </Stack>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
          Kontakt
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Handynummer"
              size="small"
              fullWidth
              value={form.handynummer ?? ''}
              onChange={(e) => updateForm('handynummer', e.target.value || null)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Festnetz / Durchwahl"
              size="small"
              fullWidth
              value={form.festnetz ?? ''}
              onChange={(e) => updateForm('festnetz', e.target.value || null)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Bevorzugtes Kontaktzeitfenster"
              size="small"
              fullWidth
              value={form.kontakt_zeitfenster ?? ''}
              onChange={(e) => updateForm('kontakt_zeitfenster', e.target.value || null)}
              helperText="z. B. werktags 9–15 Uhr"
            />
          </Grid>
        </Grid>
        <SwitchRow
          label="Kontaktdaten (Mobil, Festnetz, Zeitfenster) für Kolleginnen und Kollegen sichtbar"
          checked={form.profil_oeffentlich_kontakt}
          onChange={(v) => updateForm('profil_oeffentlich_kontakt', v)}
        />

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
          Adresse
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              label="Straße / Zusatz"
              size="small"
              fullWidth
              value={form.adresse_zeile1 ?? ''}
              onChange={(e) => updateForm('adresse_zeile1', e.target.value || null)}
            />
          </Grid>
          <Grid item xs={6} sm={4}>
            <TextField
              label="PLZ"
              size="small"
              fullWidth
              value={form.plz ?? ''}
              onChange={(e) => updateForm('plz', e.target.value || null)}
            />
          </Grid>
          <Grid item xs={6} sm={8}>
            <TextField
              label="Ort"
              size="small"
              fullWidth
              value={form.ort ?? ''}
              onChange={(e) => updateForm('ort', e.target.value || null)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Land"
              size="small"
              fullWidth
              value={form.land ?? ''}
              onChange={(e) => updateForm('land', e.target.value || null)}
            />
          </Grid>
        </Grid>
        <SwitchRow
          label="Adresse für Kolleginnen und Kollegen sichtbar"
          checked={form.profil_oeffentlich_adresse}
          onChange={(v) => updateForm('profil_oeffentlich_adresse', v)}
        />

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
          Erreichbarkeit
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Zeitzone"
              size="small"
              fullWidth
              value={form.zeitzone ?? ''}
              onChange={(e) => updateForm('zeitzone', e.target.value || null)}
              helperText="z. B. Europe/Berlin"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <TextField
                label="Sprache hinzufügen"
                size="small"
                value={spracheInput}
                onChange={(e) => setSpracheInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addSprache();
                  }
                }}
              />
              <IconButton onClick={addSprache} color="primary" sx={{ mt: 0.5 }} aria-label="Sprache hinzufügen">
                <AddIcon />
              </IconButton>
            </Stack>
            <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 1 }}>
              {form.sprachen.map((s) => (
                <Chip
                  key={s}
                  label={s}
                  size="small"
                  onDelete={() => updateForm(
                    'sprachen',
                    form.sprachen.filter((x) => x !== s),
                  )}
                />
              ))}
            </Stack>
          </Grid>
        </Grid>
        <SwitchRow
          label="Zeitzone und Sprachen für Kolleginnen und Kollegen sichtbar"
          checked={form.profil_oeffentlich_erreichbarkeit}
          onChange={(v) => updateForm('profil_oeffentlich_erreichbarkeit', v)}
        />

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
          Berufliches
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              label="Beruflicher Titel"
              size="small"
              fullWidth
              value={form.beruflicher_titel ?? ''}
              onChange={(e) => updateForm('beruflicher_titel', e.target.value || null)}
              helperText="Unabhängig von Ihrer Rollen-Zuweisung im System"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="LinkedIn (https://…)"
              size="small"
              fullWidth
              value={form.link_linkedin ?? ''}
              onChange={(e) => updateForm('link_linkedin', e.target.value || null)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Website (https://…)"
              size="small"
              fullWidth
              value={form.link_website ?? ''}
              onChange={(e) => updateForm('link_website', e.target.value || null)}
            />
          </Grid>
        </Grid>
        <SwitchRow
          label="Beruflicher Titel und Links für Kolleginnen und Kollegen sichtbar"
          checked={form.profil_oeffentlich_beruflich}
          onChange={(v) => updateForm('profil_oeffentlich_beruflich', v)}
        />

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
          Kompetenzen
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          Abschlüsse, Zertifikate und Fähigkeiten
        </Typography>

        {form.profil_abschluesse.map((a, i) => (
          <Grid container spacing={1} key={i} sx={{ mb: 1.5 }} alignItems="flex-start">
            <Grid item xs={12} sm={4}>
              <TextField
                label="Bezeichnung"
                size="small"
                fullWidth
                value={a.bezeichnung}
                onChange={(e) => updateAbschluss(i, { bezeichnung: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                label="Institution"
                size="small"
                fullWidth
                value={a.institution ?? ''}
                onChange={(e) => updateAbschluss(i, { institution: e.target.value || null })}
              />
            </Grid>
            <Grid item xs={6} sm={2}>
              <TextField
                label="Jahr"
                size="small"
                fullWidth
                type="number"
                value={a.jahr ?? ''}
                onChange={(e) =>
                  updateAbschluss(i, {
                    jahr: e.target.value === '' ? null : parseInt(e.target.value, 10),
                  })
                }
              />
            </Grid>
            <Grid item xs={5} sm={2}>
              <FormControl size="small" fullWidth>
                <InputLabel>Art</InputLabel>
                <Select
                  label="Art"
                  value={a.art ?? ''}
                  onChange={(e) =>
                    updateAbschluss(i, {
                      art: (e.target.value || null) as ProfilAbschlussArt | null,
                    })
                  }
                >
                  <MenuItem value="">—</MenuItem>
                  {ABS_ARTEN.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={1} sm={1} sx={{ display: 'flex', justifyContent: 'center' }}>
              <IconButton size="small" onClick={() => removeAbschluss(i)} aria-label="Abschluss entfernen">
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Grid>
          </Grid>
        ))}
        <Button startIcon={<AddIcon />} size="small" onClick={addAbschluss} sx={{ mb: 2 }}>
          Abschluss / Qualifikation
        </Button>

        {form.profil_faehigkeiten.map((f, i) => (
          <Grid container spacing={1} key={i} sx={{ mb: 1.5 }} alignItems="flex-start">
            <Grid item xs={12} sm={6}>
              <TextField
                label="Fähigkeit"
                size="small"
                fullWidth
                value={f.name}
                onChange={(e) => updateFaehigkeit(i, { name: e.target.value })}
              />
            </Grid>
            <Grid item xs={10} sm={4}>
              <FormControl size="small" fullWidth>
                <InputLabel>Niveau</InputLabel>
                <Select
                  label="Niveau"
                  value={f.niveau ?? ''}
                  onChange={(e) =>
                    updateFaehigkeit(i, {
                      niveau: (e.target.value || null) as ProfilFaehigkeitNiveau | null,
                    })
                  }
                >
                  <MenuItem value="">—</MenuItem>
                  {NIVEAUS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={2} sm={2} sx={{ display: 'flex', justifyContent: 'center' }}>
              <IconButton size="small" onClick={() => removeFaehigkeit(i)} aria-label="Fähigkeit entfernen">
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Grid>
          </Grid>
        ))}
        <Button startIcon={<AddIcon />} size="small" onClick={addFaehigkeit} sx={{ mb: 2 }}>
          Fähigkeit
        </Button>

        <SwitchRow
          label="Abschlüsse und Fähigkeiten für Kolleginnen und Kollegen sichtbar"
          checked={form.profil_oeffentlich_kompetenzen}
          onChange={(v) => updateForm('profil_oeffentlich_kompetenzen', v)}
        />

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
          Persönliches
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Geburtsdatum"
              type="date"
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={form.geburtsdatum ?? ''}
              onChange={(e) => updateForm('geburtsdatum', e.target.value || null)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Über mich"
              size="small"
              fullWidth
              multiline
              minRows={3}
              value={form.profil_beschreibung ?? ''}
              onChange={(e) => updateForm('profil_beschreibung', e.target.value || null)}
            />
          </Grid>
        </Grid>
        <SwitchRow
          label="Geburtsdatum für Kolleginnen und Kollegen sichtbar"
          checked={form.profil_oeffentlich_geburtsdatum}
          onChange={(v) => updateForm('profil_oeffentlich_geburtsdatum', v)}
        />
        <SwitchRow
          label="Freitext „Über mich“ für Kolleginnen und Kollegen sichtbar"
          checked={form.profil_oeffentlich_beschreibung}
          onChange={(v) => updateForm('profil_oeffentlich_beschreibung', v)}
        />

        <Box sx={{ mt: 3 }}>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            Profil speichern
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default ProfilNutzerprofilSection;
